import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../axios.js';
import { extractErrorMessage } from '../lib/error.js';
import type { GrantPayload } from '../services/user-grants.js';
import { type AppUser } from '../types';
import type { UserRole } from '../types/user.js';

type CreateUserInput = {
  username: string;
  password: string;
  role: UserRole;
  grants?: GrantPayload[];
};

export type UpdateUserInput = {
  id: string;
  username?: string;
  password?: string;
  role?: UserRole;
  grants?: GrantPayload[];
};

async function fetchUsers(): Promise<AppUser[]> {
  const { data } = await apiClient.get<AppUser[]>('/users');
  return data;
}

async function createUser(input: CreateUserInput): Promise<AppUser> {
  try {
    const { data } = await apiClient.post<AppUser>('/users', input);
    return data;
  } catch (error) {
    Sentry.captureException(error);
    throw new Error(extractErrorMessage(error, 'Failed to create user'));
  }
}

async function updateUser(input: UpdateUserInput): Promise<AppUser> {
  const { id, ...body } = input;
  try {
    const { data } = await apiClient.patch<AppUser>(`/users/${id}`, body);
    return data;
  } catch (error) {
    Sentry.captureException(error);
    throw new Error(extractErrorMessage(error, 'Failed to update user'));
  }
}

async function deleteUser(id: string): Promise<void> {
  try {
    await apiClient.delete(`/users/${id}`);
  } catch (error) {
    Sentry.captureException(error);
    throw new Error(extractErrorMessage(error, 'Failed to delete user'));
  }
}

export function useUsers(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['users'],
    queryFn: fetchUsers,
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}
